import { differenceInCalendarDays, differenceInMinutes } from 'date-fns';

import { useLocale } from '@adsk/alloy-react-locale';

import { SHORT, RelativeOptions } from './consts';
import { displayDate } from './displayDate';
import { displayTime } from './displayTime';
import { getValidLocale } from './helpers';

/**
 * `useRelativeDate` takes a `Date` object, and returns a string illustrating the date.
 * Dates with a difference greater than 24 hours can be displayed in either a 'short' or 'numeric' format.
 * If necessary, user-specified start dates may be implemented instead of the current date.
 * @param date - `Date` object representing the relative date and time.
 * @param startDate - `Date` object representing the current date and time.
 * @param options - options object can contain following fields:
 *   * `format` - either "default", "short", or "numeric".
 *   * `locale` - developer designated locale (this will take precedence over browser setting, if language differs).
 * @returns a string illustrating `date` relative to the current date, or if applicable, `startDate`.
 */
const useRelativeDate = (
  date: Date,
  startDate?: Date,
  options?: RelativeOptions,
) => {
  startDate = startDate ?? new Date(Date.now());
  const diffMinutes = differenceInMinutes(startDate, date);
  const diffDays = differenceInCalendarDays(startDate, date);
  const format = options?.format;
  const { T } = useLocale(({ locale }) =>
    require(
      `./i18n/${
        options?.locale ? getValidLocale(options.locale) : locale
      }.json`,
    ),
  );

  if (diffMinutes <= 1) {
    return T.translate('MOMENTS_AGO') as string;
  } else if (diffMinutes > 1 && diffMinutes < 60) {
    return T.translate('MINUTES_AGO', { minutes: diffMinutes }) as string;
  } else if (diffMinutes >= 60 && diffDays === 0) {
    return T.translate('TODAY_AT', {
      time: displayTime(date, { format: 'short', utc: false }),
    }) as string;
  } else if (diffDays === 1) {
    return T.translate('YESTERDAY_AT', {
      time: displayTime(date, { format: 'short', utc: false }),
    }) as string;
  } else if (format === SHORT) {
    return T.translate('DATE_AT_TIME', {
      date: displayDate(date, {
        format: 'short',
        utc: false,
        year: true,
      }),
      time: displayTime(date, { format: 'short', utc: false }),
    }) as string;
  } else {
    return T.translate('DATE_TIME', {
      date: displayDate(date, { format: 'numeric', utc: false }),
      time: displayTime(date, { format: 'short', utc: false }),
    }) as string;
  }
};

export default useRelativeDate;
